import { useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import ProjectsList from './components/ProjectsList';
import NewProject from './components/NewProject';
import ProjectView from './components/ProjectView';
import NewArea from './components/NewArea';
import FlatCalc from './components/FlatCalc';
import AdminDashboard from './components/AdminDashboard';

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function Header() {
  return (
    <header className="bg-slate-800 border-b border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <span className="text-slate-900 font-bold text-lg">FQ</span>
            </div>
            <span className="font-semibold text-white text-lg">FlatQuote</span>
            <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-0.5 rounded-full font-medium">
              BETA
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <SignedIn>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-4 py-2 rounded-lg transition-colors">
                  Sign In
                </button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </div>
    </header>
  );
}

function LandingPage() {
  return (
    <div className="text-center py-16">
      <h1 className="text-4xl font-bold text-white mb-4">
        Professional Flat Roofing Estimates
      </h1>
      <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
        Create accurate estimates for single ply, bituminous, and liquid applied 
        flat roofing systems. Built by roofers, for roofers.
      </p>
      <SignInButton mode="modal">
        <button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 py-3 rounded-lg text-lg transition-colors">
          Get Started Free
        </button>
      </SignInButton>
      <p className="text-sm text-slate-500 mt-4">
        Free during beta • No credit card required
      </p>
    </div>
  );
}

// Auth guard component - shows landing page when signed out
function AuthGuard({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  
  if (!isLoaded) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
      </div>
    );
  }
  
  if (!isSignedIn) {
    return <LandingPage />;
  }
  
  return children;
}

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);

  if (isLoading) {
    return <LoadingScreen onReady={() => setIsLoading(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/admin" element={<AuthGuard><AdminDashboard /></AuthGuard>} />
          <Route path="/" element={<AuthGuard><ProjectsList /></AuthGuard>} />
          <Route path="/project/new" element={<AuthGuard><NewProject /></AuthGuard>} />
          <Route path="/project/:id" element={<AuthGuard><ProjectView /></AuthGuard>} />
          <Route path="/project/:projectId/area/new" element={<AuthGuard><NewArea /></AuthGuard>} />
          <Route path="/project/:projectId/area/:areaId" element={<AuthGuard><FlatCalc /></AuthGuard>} />
        </Routes>
      </main>

      <footer className="border-t border-slate-700 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center text-sm text-slate-500">
            <p>© 2025 FlatQuote. All rights reserved.</p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-slate-300">Privacy</a>
              <a href="#" className="hover:text-slate-300">Terms</a>
              <a href="#" className="hover:text-slate-300">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  if (!CLERK_KEY) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-800 mb-2">Configuration Error</h1>
          <p className="text-red-600">Missing VITE_CLERK_PUBLISHABLE_KEY environment variable</p>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <ErrorBoundary>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ErrorBoundary>
    </ClerkProvider>
  );
}

export default App;
